import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Cacheable } from 'ts-cacheable';

import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { DocumentResponse } from '@mitsubishi/documents/data-access';
import { ApiUrlInjectionToken, EntityFileResponse } from '@mitsubishi/shared/data-access';
import { FileTransferService } from '@mitsubishi/utils';

import { News, NewsDocumentRequest, NewsFilters, NewsRequest, NewsStatuses, NewsTag } from '../models';

@Injectable({
  providedIn: 'root',
})
export class NewsService {
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    private uploadUtilsService: FileTransferService,
    @Inject(ApiUrlInjectionToken) apiServerUrl: string
  ) {
    this.baseUrl = apiServerUrl + '/knowledgebase';
  }

  @Cacheable({
    maxCacheCount: 1000,
  })
  getCachedNewsImageUrl(fileName: string): Observable<string> {
    return this.http.get<EntityFileResponse>(`${this.baseUrl}/get/${fileName}`).pipe(map(response => response.url));
  }

  getNewsImageUrl(fileName: string): Observable<string> {
    return this.http.get<EntityFileResponse>(`${this.baseUrl}/get/${fileName}`).pipe(map(response => response.url));
  }

  getRelevantNews(): Observable<News[] | null> {
    return this.http.get<News[] | null>(`${this.baseUrl}/dashboard`);
  }

  @Cacheable()
  getNewsStatuses(): Observable<NewsStatuses[]> {
    return this.http.get<NewsStatuses[]>(`${this.baseUrl}/statuses`);
  }

  getNewsTags(param = ''): Observable<NewsTag[]> {
    return this.http.post<NewsTag[]>(`${this.baseUrl}/free-tags`, {
      filter: param,
    });
  }

  getNewsModelsTags(param: string): Observable<NewsTag[]> {
    return this.http.get<NewsTag[]>(`${this.baseUrl}-tag/models?search_param=${param}`);
  }

  getNewsFamiliesTags(param: string): Observable<NewsTag[]> {
    return this.http.get<NewsTag[]>(`${this.baseUrl}-tag/families?search_param=${param}`);
  }

  getNewsSeriesTags(param: string): Observable<NewsTag[]> {
    return this.http.get<NewsTag[]>(`${this.baseUrl}-tag/series?search_param=${param}`);
  }

  getUsersTags(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}-visibility/users`).pipe();
  }

  @Cacheable()
  getOrganisationsTags(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}-visibility/organisations`);
  }

  @Cacheable()
  getCountriesTags(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}-visibility/countries`);
  }

  @Cacheable()
  getOrgTypesTags(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}-visibility/org-types`);
  }

  getNewsWithFilters(request: NewsFilters): Observable<News[]> {
    return this.http.post<News[]>(`${this.baseUrl}/search`, request);
  }

  getNewsAdminWithFilters(request: NewsFilters): Observable<News[]> {
    return this.http.post<News[]>(`${this.baseUrl}/search-admin`, request);
  }

  addNews(request: NewsRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}`, request);
  }

  updateNews(newsId: string, request: NewsRequest): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/${newsId}`, request);
  }

  getNewsDetail(id: string): Observable<News> {
    return this.http.get<News>(`${this.baseUrl}/${id}`);
  }

  getDocumentUrl(fileName: string): Observable<DocumentResponse> {
    const encodedFilename = encodeURIComponent(fileName);
    return this.http.get<DocumentResponse>(`${this.baseUrl}/get/${encodedFilename}`);
  }

  uploadImage(file: File, entityId: string): Observable<null> {
    return this.getUploadUrl(file.name).pipe(
      map(resp => resp.url),
      switchMap((uploadUrl: string) => {
        return this.uploadUtilsService.uploadFileWithPresignedUrl(uploadUrl, file);
      }),
      switchMap(result => {
        return this.consolidateImage(entityId, file.name);
      })
    );
  }

  private getUploadUrl(fileName: string): Observable<DocumentResponse> {
    const encodedFilename = encodeURIComponent(fileName);
    return this.http.get<DocumentResponse>(`${this.baseUrl}/put/${encodedFilename}`);
  }

  private consolidateImage(entityUuid: string, fileName: string): Observable<null> {
    const request: NewsDocumentRequest = {
      entityUuid,
      fileName,
    };
    return this.http.put<null>(`${this.baseUrl}/consolidate`, request);
  }

  deleteNews(newsUuid: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${newsUuid}`);
  }

  duplicateNews(newsUuid: string): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${newsUuid}/duplicate`, null);
  }

  setNewsAsRead(projectId: string, selectionId: string, newsId: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${projectId}/${selectionId}/${newsId}/read`);
  }
}
